/**
 * Which taxes this deployment tracks, and how. Pure — the backend keeps an
 * identical copy in utils/taxSettings.js; keep both in sync.
 *
 * GotaSettings.taxes (admin: Configuración › Contabilidad, SHOW_TAX_SETTINGS):
 *   { irpf: { enabled }, iva: { enabled, rate, included } }
 * The IRPF rate stays in the legacy GotaSettings.irpf field.
 *
 * With no `taxes` stored the result is exactly the legacy behaviour: IRPF
 * always shown and withheld at GotaSettings.irpf, IVA not tracked.
 */
export const DEFAULT_IVA_RATE = 22;

const toRate = (value, fallback) => {
	const n = Number(value);
	return Number.isFinite(n) && n >= 0 ? n : fallback;
};

export const resolveTaxes = (settings) => {
	const taxes = (settings && settings.taxes) || {};
	const irpf = taxes.irpf || {};
	const iva = taxes.iva || {};
	const facturacion = (settings && settings.facturacion) || {};
	return {
		irpf: {
			enabled: irpf.enabled !== false,
			rate: toRate(settings && settings.irpf, 0),
		},
		iva: {
			enabled: iva.enabled === true,
			rate: toRate(iva.rate, DEFAULT_IVA_RATE),
			// Amounts already include IVA unless said otherwise; facturación's
			// montosBrutos is the same question asked for invoicing.
			included: iva.included !== undefined ? iva.included !== false : facturacion.montosBrutos !== false,
		},
	};
};

/** The IRPF rate the balance withholds: 0 when IRPF is not tracked. */
export const effectiveIrpfRate = (taxes) => (taxes.irpf.enabled ? taxes.irpf.rate : 0);

/**
 * Splits a total into net + IVA. `included`: the total already carries IVA
 * (gross); otherwise IVA is added on top of it.
 */
export const splitIva = (total, { rate, included }) => {
	const r = rate / 100;
	if (included) {
		const net = total / (1 + r);
		return { net, iva: total - net, gross: total };
	}
	return { net: total, iva: total * r, gross: total * (1 + r) };
};

/** Normalizes an admin payload into the stored shape; drops unknown keys. */
export const sanitizeTaxes = (input) => {
	if (!input || typeof input !== 'object') return undefined;
	const out = {};
	if (input.irpf && typeof input.irpf === 'object') {
		out.irpf = { enabled: input.irpf.enabled !== false };
	}
	if (input.iva && typeof input.iva === 'object') {
		out.iva = {
			enabled: input.iva.enabled === true,
			rate: toRate(input.iva.rate, DEFAULT_IVA_RATE),
			included: input.iva.included !== false,
		};
	}
	return out;
};
